import { useCallback, useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconInfoCircle,
  IconPencil,
  IconPlus,
} from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import type { IdentityStatus, Student } from '@pta/types';
import { fetchStudentsByGroup } from '../api/core-entities';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { StudentFormModal } from '../components/StudentFormModal';

/**
 * Csoport diákjainak kezelő oldala (pta_leader).
 *
 * - Lista a `GET /groups/:id/students` aktív tagjaiból (`asOf=now`).
 * - "Új diák" gomb → StudentFormModal (új mode).
 * - Soronként szerkesztés ikon → StudentFormModal (edit mode); free_text
 *   csoportos diák szerkesztését a modal jelzi és tiltja.
 * - Identity-status badge.
 *
 * Az oldal a /app/groups/:id/students route alatt él. A `:id` a paramban,
 * az aktív group-pal való szinkronra is figyel — ha más groupId van az URL-ben,
 * mint az `activeGroupId`, akkor az oldal váltja az aktív csoportot.
 */
export function GroupStudentsPage() {
  const params = useParams();
  const groupId = Number(params.id);
  const navigate = useNavigate();
  const { myGroups, activeGroupId, setActiveGroupId } = useAuth();

  const groupEntry = myGroups.find((g) => g.id === groupId);
  const isFreeText = groupEntry?.institutionBindingStatus === 'free_text';

  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpened, setCreateOpened] = useState(false);
  const [editTarget, setEditTarget] = useState<Student | null>(null);

  // URL → activeGroupId szinkron, hogy a layout (Picker) is kövesse.
  useEffect(() => {
    if (Number.isFinite(groupId) && groupId !== activeGroupId) {
      setActiveGroupId(groupId);
    }
  }, [groupId, activeGroupId, setActiveGroupId]);

  const reload = useCallback(async () => {
    if (!Number.isFinite(groupId)) return;
    setLoading(true);
    try {
      const list = await fetchStudentsByGroup(groupId);
      setStudents(list);
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!Number.isFinite(groupId)) {
    return (
      <Container size="lg">
        <Alert color="red">Érvénytelen csoport-azonosító az URL-ben.</Alert>
      </Container>
    );
  }

  return (
    <Container size="lg">
      <Stack>
        <Group justify="space-between" align="end">
          <Stack gap={0}>
            <Anchor
              component="button"
              type="button"
              onClick={() => navigate('/app/my-groups')}
              size="sm"
            >
              <Group gap={4}>
                <IconArrowLeft size={14} />
                Saját csoportjaim
              </Group>
            </Anchor>
            <Title order={2}>
              {groupEntry?.name ?? `Csoport #${groupId}`} — diákok
            </Title>
            {groupEntry?.institutionLabel && (
              <Text size="sm" c="dimmed">
                {groupEntry.institutionLabel}
              </Text>
            )}
          </Stack>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setCreateOpened(true)}
          >
            Új diák
          </Button>
        </Group>

        {isFreeText && (
          <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
            <Text size="sm">
              Ez a csoport <strong>szabad szöveges iskolához</strong> van kötve.
              Új diák felvehető informal vagy identified azonosítóval; a meglévő
              diák szerkesztése admin által történik.
            </Text>
          </Alert>
        )}

        <Card withBorder p={0}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Diák</Table.Th>
                <Table.Th>Oktatási azonosító</Table.Th>
                <Table.Th>Státusz</Table.Th>
                <Table.Th style={{ width: 100 }}>Műveletek</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center">
                      Betöltés...
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {!loading && students.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center">
                      Még nincs diák a csoportban. Adj hozzá egyet az „Új diák”
                      gombbal.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {!loading &&
                students.map((s) => (
                  <Table.Tr key={s.id}>
                    <Table.Td>
                      <Text fw={500}>
                        {s.lastName} {s.firstName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c={s.educationalId ? undefined : 'dimmed'}>
                        {s.educationalId ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <IdentityStatusBadge status={s.identityStatus} />
                    </Table.Td>
                    <Table.Td>
                      <Tooltip
                        label={
                          isFreeText
                            ? 'Free_text csoportos diákot egyelőre csak admin szerkeszthet.'
                            : 'Szerkesztés'
                        }
                      >
                        <ActionIcon
                          variant="light"
                          disabled={isFreeText}
                          onClick={() => setEditTarget(s)}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
            </Table.Tbody>
          </Table>
        </Card>
      </Stack>

      <StudentFormModal
        opened={createOpened}
        onClose={() => setCreateOpened(false)}
        groupId={groupId}
        isFreeTextGroup={isFreeText ?? false}
        onSaved={async () => {
          setCreateOpened(false);
          await reload();
        }}
      />

      {editTarget && (
        <StudentFormModal
          opened={true}
          onClose={() => setEditTarget(null)}
          groupId={groupId}
          student={editTarget}
          isFreeTextGroup={isFreeText ?? false}
          onSaved={async () => {
            setEditTarget(null);
            await reload();
          }}
        />
      )}
    </Container>
  );
}

function IdentityStatusBadge({ status }: { status: IdentityStatus }) {
  switch (status) {
    case 'informal':
      return (
        <Badge color="gray" variant="light">
          Informal
        </Badge>
      );
    case 'identified':
      return (
        <Badge color="teal" variant="light">
          Azonosított
        </Badge>
      );
    case 'verified':
      return (
        <Badge color="indigo" variant="light">
          Hitelesített
        </Badge>
      );
    default:
      return null;
  }
}
